import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from "../context/LanguageContext.tsx";
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Home, MapPin, DollarSign, Bed, Bath, Square, Calendar, Phone, Mail, User, Image, X, CheckCircle, Edit } from 'lucide-react';

interface PropertyFormData {
  title: string;
  description: string;
  price: number;
  type: 'apartment' | 'house' | 'condo' | 'land' | 'commercial';
  status: 'sale' | 'rent';
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  images: File[];
  features: {
    bedrooms: number;
    bathrooms: number;
    area: number;
    garage: boolean;
    yearBuilt: number;
  };
  amenities: string[];
  contactInfo: {
    name: string;
    email: string;
    phone: string;
  };
}

interface ListingImage {
  id: string;
  publicUrl: string;
  position: number;
}

const EditProperty: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<PropertyFormData>({
    title: '',
    description: '',
    price: 0,
    type: 'apartment',
    status: 'sale',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
    images: [],
    features: {
      bedrooms: 0,
      bathrooms: 0,
      area: 0,
      garage: false,
      yearBuilt: new Date().getFullYear(),
    },
    amenities: [],
    contactInfo: {
      name: '',
      email: '',
      phone: '',
    },
  });

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [existingImages, setExistingImages] = useState<ListingImage[]>([]);
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);
  const [imageActionError, setImageActionError] = useState<string | null>(null);

  useEffect(() => {
    const fetchListing = async () => {
      if (!id) {
        setError(t('home.errors.failedToLoadProperty'));
        setLoading(false);
        return;
      }
      if (!user) {
        setLoading(false);
        const redirectTo = `${location.pathname}${location.search}${location.hash}`;
        navigate(`/login?redirect=${encodeURIComponent(redirectTo)}`);
        return;
      }
      
      try {
        setLoading(true);
        const listing = await apiService.getMyListing(id);
        
        const mappedImages: ListingImage[] = Array.isArray(listing.images)
          ? listing.images
              .map((img: any) => ({
                id: String(img.id),
                publicUrl: img.public_url ?? img.publicUrl ?? img.url ?? '',
                position: Number(img.position ?? 0),
              }))
              .filter((img: ListingImage) => img.id && img.publicUrl)
              .sort((a: ListingImage, b: ListingImage) => a.position - b.position)
          : [];

        setFormData({
          title: listing.title || '',
          description: listing.description || '',
          price: listing.price || 0,
          type: 'apartment',
          status: listing.purpose === 'RENT' ? 'rent' : 'sale',
          address: {
            street: listing.address || '',
            city: listing.city || '',
            state: '',
            zipCode: listing.zip_code || listing.zipCode || '',
            country: listing.country || '',
          },
          images: [],
          features: {
            bedrooms: listing.rooms || 0,
            bathrooms: listing.bathrooms || 0,
            area: Number(listing.area_m2 || listing.areaM2 || 0),
            garage: false,
            yearBuilt: new Date().getFullYear(),
          },
          amenities: [],
          contactInfo: {
            name: listing.contact_name || listing.contactName || '',
            email: listing.contact_email || listing.contactEmail || '',
            phone: listing.contact_phone || listing.contactPhone || '',
          },
        });
        setExistingImages(mappedImages);
      } catch (err) {
        setError(t('home.errors.failedToLoadProperty'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id, user, navigate, t, location.pathname, location.search, location.hash]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof PropertyFormData] as Record<string, any>),
          [child]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
      }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData(prev => ({
        ...prev,
        images: [...Array.from(e.target.files || [])],
      }));
    }
  };

  const handleDeleteExistingImage = async (imageId: string) => {
    if (!id || deletingImageId) return;
    const confirmed = window.confirm(t('profile.deleteImageConfirm'));
    if (!confirmed) return;

    try {
      setDeletingImageId(imageId);
      setImageActionError(null);
      await apiService.deleteImage(id, imageId);
      setExistingImages(prev => prev.filter(image => image.id !== imageId));
    } catch (err) {
      console.error('Failed to delete image:', err);
      setImageActionError(t('profile.deleteImageFailed'));
    } finally {
      setDeletingImageId(null);
    }
  };

  const handleSubmit = async () => {
    if (!user || !id) {
      navigate('/login');
      return;
    }

    try {
      setIsSubmitting(true);
      setBlockedMessage(null);
      await apiService.updateListing(id, {
        title: formData.title,
        description: formData.description,
        price: formData.price,
        currency: 'EUR',
        address: formData.address.street,
        city: formData.address.city,
        country: formData.address.country,
        zipCode: formData.address.zipCode,
        areaM2: formData.features.area,
        rooms: formData.features.bedrooms,
        bathrooms: formData.features.bathrooms,
        purpose: formData.status === 'rent' ? 'RENT' : 'SALE',
        contactName: formData.contactInfo.name,
        contactEmail: formData.contactInfo.email,
        contactPhone: formData.contactInfo.phone,
      });

      // Upload new images if any
      for (const file of formData.images) {
        if (!['image/jpeg','image/png','image/webp'].includes(file.type)) continue;
        await apiService.uploadImage(id, file);
      }

      // Show success toast
      setShowSuccessToast(true);
      
      // Navigate after short delay
      setTimeout(() => {
        navigate('/my-properties');
      }, 1500);
    } catch (error) {
      console.error('Failed to update listing:', error);
      const message = error instanceof Error ? error.message : '';
      if (/blokiran/i.test(message) || message === 'BLOCKED_ACCOUNT') {
        setBlockedMessage(t('auth.blockedAccount'));
      } else {
        alert(t('home.errors.failedToUpdate'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 4));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Greška</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/my-properties')}
            className="px-6 py-3 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-all"
          >
            Nazad na nekretnine
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-dark mb-4">
            <Edit className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            {t('profile.editProperty')}
          </h1>
          <p className="text-lg text-gray-600">
            Ažurirajte informacije o vašoj nekretnini
          </p>
        </div>

        {blockedMessage && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {blockedMessage}
          </div>
        )}

        {/* Progress Steps */}
        <div className="mb-10">
          <div className="relative max-w-3xl mx-auto">
            {/* Progress Line */}
            <div className="absolute top-6 left-0 right-0 h-1 bg-gray-200" style={{ left: 'calc(12.5%)', right: 'calc(12.5%)' }}>
              <div 
                className="h-full bg-primary-500 transition-all duration-300"
                style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
              />
            </div>
            
            {/* Step Circles */}
            <div className="relative grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex justify-center">
                  <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                    currentStep >= step 
                      ? 'bg-primary-500 border-primary-500 text-white' 
                      : 'bg-white border-gray-300 text-gray-400'
                  } font-semibold transition-all duration-300 z-10 relative`}>
                    {currentStep > step ? <CheckCircle className="w-6 h-6" /> : step}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Step Labels */}
            <div className="grid grid-cols-4 gap-4 mt-4">
              <span className="text-xs font-medium text-gray-600 text-center">{t('property.addProperty.steps.basicInfo')}</span>
              <span className="text-xs font-medium text-gray-600 text-center">{t('property.addProperty.steps.specifications')}</span>
              <span className="text-xs font-medium text-gray-600 text-center">{t('property.addProperty.steps.location')}</span>
              <span className="text-xs font-medium text-gray-600 text-center">{t('property.addProperty.steps.contact')}</span>
            </div>
          </div>
        </div>

        <form 
          onSubmit={(e) => e.preventDefault()}
          className="space-y-8"
        >
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 animate-fade-in">
              <div className="flex items-center mb-6">
                <Home className="w-6 h-6 text-primary-500 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('property.addProperty.basicInfo')}
                </h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('property.addProperty.propertyTitle')} *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('property.addProperty.propertyType')} *
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                      required
                    >
                      <option value="apartment">{t('property.type.apartment')}</option>
                      <option value="house">{t('property.type.house')}</option>
                      <option value="condo">{t('property.type.condo')}</option>
                      <option value="land">{t('property.type.land')}</option>
                      <option value="commercial">{t('property.type.commercial')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('property.addProperty.propertyStatus')} *
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                      required
                    >
                      <option value="sale">{t('property.addProperty.forSale')}</option>
                      <option value="rent">{t('property.addProperty.forRent')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <DollarSign className="w-4 h-4 inline mr-1" />
                    {t('property.addProperty.propertyPrice')} (EUR) *
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('property.addProperty.propertyDescription')} *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none resize-none"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Specifications */}
          {currentStep === 2 && (
            <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 animate-fade-in">
              <div className="flex items-center mb-6">
                <Home className="w-6 h-6 text-primary-500 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('property.addProperty.specifications')}
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Bed className="w-4 h-4 inline mr-1" />
                    {t('property.addProperty.bedrooms')} *
                  </label>
                  <input
                    type="number"
                    name="features.bedrooms"
                    value={formData.features.bedrooms}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Bath className="w-4 h-4 inline mr-1" />
                    {t('property.addProperty.bathrooms')} *
                  </label>
                  <input
                    type="number"
                    name="features.bathrooms"
                    value={formData.features.bathrooms}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Square className="w-4 h-4 inline mr-1" />
                    {t('property.addProperty.area')} *
                  </label>
                  <input
                    type="number"
                    name="features.area"
                    value={formData.features.area}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    {t('property.addProperty.yearBuilt')}
                  </label>
                  <input
                    type="number"
                    name="features.yearBuilt"
                    value={formData.features.yearBuilt}
                    onChange={handleInputChange}
                    min="1900"
                    max={new Date().getFullYear()}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                  />
                </div>
              </div>

              <div className="mt-6 flex items-center">
                <input
                  type="checkbox"
                  name="features.garage"
                  checked={formData.features.garage}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                />
                <label className="ml-3 text-sm font-medium text-gray-700">
                  {t('property.addProperty.garage')}
                </label>
              </div>

              <div className="mt-8">
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('profile.existingImages')}
                  </label>
                  {imageActionError && (
                    <p className="text-sm text-red-600 mb-3">{imageActionError}</p>
                  )}
                  {existingImages.length === 0 ? (
                    <p className="text-sm text-gray-500">{t('profile.noImages')}</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {existingImages.map((image) => (
                        <div
                          key={image.id}
                          className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50 group"
                        >
                          <img
                            src={image.publicUrl}
                            alt={formData.title || t('profile.propertyImageAlt')}
                            className="w-full h-32 object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => handleDeleteExistingImage(image.id)}
                            disabled={deletingImageId === image.id}
                            className="absolute top-2 right-2 rounded-full bg-white/90 text-red-600 p-2 shadow hover:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            title={t('common.delete')}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Image className="w-4 h-4 inline mr-1" />
                  {t('profile.addNewImages')}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-primary-500 transition-colors cursor-pointer">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" className="cursor-pointer">
                    <Image className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-sm text-gray-600">
                      {t('property.addProperty.uploadInstructions')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('property.addProperty.uploadLimit')}
                    </p>
                    {formData.images.length > 0 && (
                      <p className="text-sm font-medium text-primary-500 mt-3">
                        {formData.images.length} {t('property.addProperty.newImagesSelected')}
                      </p>
                    )}
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Location */}
          {currentStep === 3 && (
            <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 animate-fade-in">
              <div className="flex items-center mb-6">
                <MapPin className="w-6 h-6 text-primary-500 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('property.addProperty.location')}
                </h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('property.addProperty.streetAddress')} *
                  </label>
                  <input
                    type="text"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('property.addProperty.city')} *
                    </label>
                    <input
                      type="text"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('property.addProperty.country')} *
                    </label>
                    <input
                      type="text"
                      name="address.country"
                      value={formData.address.country}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                      required
                    />
                  </div>
                </div>

                {/* ZIP Code */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('property.addProperty.zipCode')}
                  </label>
                  <input
                    type="text"
                    name="address.zipCode"
                    value={formData.address.zipCode}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Contact Information */}
          {currentStep === 4 && (
            <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 animate-fade-in">
              <div className="flex items-center mb-6">
                <User className="w-6 h-6 text-primary-500 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('property.addProperty.contactInfo')}
                </h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-1" />
                    {t('property.addProperty.contactName')} *
                  </label>
                  <input
                    type="text"
                    name="contactInfo.name"
                    value={formData.contactInfo.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    {t('property.addProperty.contactEmail')} *
                  </label>
                  <input
                    type="email"
                    name="contactInfo.email"
                    value={formData.contactInfo.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    {t('property.addProperty.contactPhone')} *
                  </label>
                  <input
                    type="tel"
                    name="contactInfo.phone"
                    value={formData.contactInfo.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                    required
                  />
                </div>

                <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 mt-6">
                  <p className="text-sm text-primary-700">
                    <strong>Napomena:</strong> Ove kontakt informacije će biti vidljive samo korisnicima koji kupe pristup tokenima.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center pt-6">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ← {t('common.back')}
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate('/my-properties')}
                className="px-6 py-3 rounded-xl font-semibold text-gray-700 bg-white border-2 border-gray-200 hover:bg-gray-50 transition-all"
              >
                {t('common.cancel')}
              </button>

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-8 py-3 rounded-xl font-semibold text-white bg-brand-dark hover:bg-secondary-700 transition-all shadow-lg hover:shadow-xl"
                >
                  Sledeće →
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-8 py-3 rounded-xl font-semibold text-white bg-primary-500 hover:bg-primary-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('property.addProperty.saving') : t('profile.saveChanges')}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Success Toast */}
      {showSuccessToast && (
        <div className="fixed top-8 right-8 z-50 animate-slide-in">
          <div className="bg-primary-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
            <CheckCircle className="w-6 h-6" />
            <div>
              <p className="font-semibold">{t('property.addProperty.successUpdated')}</p>
              <p className="text-sm text-primary-100">{t('property.addProperty.redirecting')}</p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EditProperty;
